import { useEffect, useState } from "react";
import ComissionContractsAPI from '../api/ComissionContractsAPI';
import { CommissionContractTemplate } from '../types/CommissionContractTemplate';

export type Instalment = {
  percentage: number;
  method: 'Card' | 'Transfer' | 'Cash';
};

type GeneralData = {
  companyName: string;
  companyAddress: string;
  taxID: string;
  logoUrl: string;
};

type Conditions = {
  conditions: string[];
};

type PaymentMethod = {
  instalments: Instalment[];
  paymentSpecifications: string;
};

type AdditionalRequirements = {
  requiresFollowUpMeeting: boolean;
  requiresEmployeeSignature: boolean;
};

// Commission-specific fields from the Google Form
export type CommissionContract = {
  clientFirstName: string;
  clientLastName: string;
  clientPhoneNumber: string;
  clientEmail: string;
  clientSocialNick?: string;
  dressModel: string;
  originalPurchasePrice: string;
  desiredSalePriceRange: string;
  dressPhotos: string[]; // or File[] if handling uploads
  accessoryPhotos: string[]; // or File[]
  dropFee?: { price: number; currency: string };
  renewalFee?: { price: number; currency: string };
};

export type ContractTemplate = {
  general: GeneralData;
  conditions: Conditions;
  payment: PaymentMethod;
  additionalRequirements: AdditionalRequirements;
  locale: string;
};

type UseContractTemplateReturnType = {
  contractTemplate: CommissionContractTemplate;
  updateContractTemplate: (updatedContractTemplate: Partial<CommissionContractTemplate>) => void;
  updateContractTemplateLocal: (updatedContractTemplate: Partial<CommissionContractTemplate>) => void;
  locale: string;
  setLocale: any;
};

export function useCommissionContractTemplate(): UseContractTemplateReturnType {
  // TODO: for now we hardcode the locale to EN_US, because we only want to use one template for now
  const [locale, setLocale] = useState<string>('EN_US');

  const [contractTemplate, setContractTemplate] = useState<CommissionContractTemplate>({
    conditions: [],
    locale: "EN_US",
    saleDuration: 90,
    language: "EN_US",
    dropFee: { price: 0, currency: 'EUR' },
    renewalFee: { price: 0, currency: 'EUR' },
    enabled: false,
  });

  useEffect(() => {
    ComissionContractsAPI.getTemplate(locale).then(response => {
      setContractTemplate(response.data);
    }).catch(error => {
      console.error('Error fetching commission contract template:', error);
    });
  }, [locale]);

  const updateContractTemplate = (updatedContractTemplate: Partial<CommissionContractTemplate>) => {
    const completeTemplate: CommissionContractTemplate = {
      ...contractTemplate,
      ...updatedContractTemplate
    };

    const filteredTemplate = {
      locale: completeTemplate.locale,
      saleDuration: completeTemplate.saleDuration,
      language: completeTemplate.language,
      dropFee: completeTemplate.dropFee,
      renewalFee: completeTemplate.renewalFee,
      conditions: Array.isArray(completeTemplate.conditions) ? completeTemplate.conditions : [],
      formConditions: Array.isArray(completeTemplate.formConditions) ? completeTemplate.formConditions : [],
      salonSplitPercentage: completeTemplate.salonSplitPercentage,
      brideSplitPercentage: completeTemplate.brideSplitPercentage,
      enabled: completeTemplate.enabled,
      // add accessories if needed
    };

    setContractTemplate(completeTemplate);
    ComissionContractsAPI.saveTemplate(locale, filteredTemplate).catch(error => {
      console.error('Error saving commission contract template:', error);
    });
  };

  const updateContractTemplateLocal = (updatedContractTemplate: Partial<CommissionContractTemplate>) => {
    const completeTemplate: CommissionContractTemplate = {
      ...contractTemplate,
      ...updatedContractTemplate
    };
    setContractTemplate(completeTemplate);
  };

  return {
    contractTemplate,
    updateContractTemplate,
    updateContractTemplateLocal,
    locale,
    setLocale
  };
} 